// Which locale numeric dates are formatted in, and which number goes first — the third copy of a
// rule shared with iOS (`TopBarGreetingDate.dateFormattingLocale` / `.localeIsMonthFirst`) and
// Android (`ui/Components.kt`). Keep the three identical.

// The ACCOUNT-REGION OVERRIDE rule: a locale that keeps the device/browser LANGUAGE but swaps in the
// account's REGION. Kept as the third copy of a rule shared with native so the surface that needs it —
// the Android Auto car (`BriefArtwork`), which has NO user locale to read (`Locale.current` → en_US) —
// resolves month-vs-day order the same way everywhere.
//
// ⚠️ The web header (`GreetingDate`) and the two native phone headers deliberately DO NOT apply this —
// they follow the DEVICE/browser locale directly. Applying it to a phone/browser made a US-region
// account render month-first for every device language (a Hebrew phone still showing `08.31`), ignoring
// the user's own chosen format. This override is only right where there is no user locale at all (the
// car). Do not reintroduce it into a surface that has one.
//
// LANGUAGE stays the browser's, always. The account's `languageCode` is the site's content language and
// is resolved separately (`resolveInitialLocale`); borrowing it here would let a formatting concern
// change the words on the page. Only the REGION is taken. `accountCountry` null → the device locale
// unchanged.
export function dateFormattingLocale(device: string | undefined, accountCountry: string | null | undefined): string | undefined {
  // TWO LETTERS, checked — not merely two characters. Android's `Locale.Builder` THROWS on a 2-char
  // non-alpha region, so the same check has to be here or the three surfaces stop agreeing on the one
  // input where they can differ.
  const region = accountCountry?.trim().toUpperCase();
  if (!region || !/^[A-Z]{2}$/.test(region)) return device;
  const language = device?.split('-')[0];
  if (!language) return `und-${region}`;
  return `${language}-${region}`;
}

// Whether `locale` orders the month before the day in an all-numeric date (en-US "M/d" → true;
// en-GB "dd/MM", de-DE "dd.MM." → false). Falls back to day-first when the probe throws or either
// field is absent — the same default both native helpers document.
export function localeIsMonthFirst(locale: string | undefined, now: Date): boolean {
  try {
    const parts = new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' }).formatToParts(now);
    const m = parts.findIndex((p) => p.type === 'month');
    const d = parts.findIndex((p) => p.type === 'day');
    return m !== -1 && d !== -1 && m < d;
  } catch {
    return false;
  }
}

// Time zones inside a country that writes the month first: the US and its territories, Canada, the
// Philippines, Micronesia, the Marshall Islands and Palau. Every other zone is in a day-first country, Latin
// America included. Built from the IANA zone.tab for those country codes, plus the older alias names a
// browser may still report (`America/Indianapolis`, `Pacific/Truk`).
const MONTH_FIRST_ZONE_PREFIXES = ['US/', 'Canada/'];
const MONTH_FIRST_ZONES = new Set([
  'America/Adak', 'America/Anchorage', 'America/Atikokan', 'America/Atka', 'America/Blanc-Sablon',
  'America/Boise', 'America/Cambridge_Bay', 'America/Chicago', 'America/Coral_Harbour', 'America/Creston',
  'America/Dawson', 'America/Dawson_Creek', 'America/Denver', 'America/Detroit', 'America/Edmonton',
  'America/Fort_Nelson', 'America/Fort_Wayne', 'America/Glace_Bay', 'America/Goose_Bay', 'America/Halifax',
  'America/Indiana/Indianapolis', 'America/Indiana/Knox', 'America/Indiana/Marengo',
  'America/Indiana/Petersburg', 'America/Indiana/Tell_City', 'America/Indiana/Vevay',
  'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Indianapolis', 'America/Inuvik',
  'America/Iqaluit', 'America/Juneau', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello',
  'America/Knox_IN', 'America/Los_Angeles', 'America/Louisville', 'America/Menominee', 'America/Metlakatla',
  'America/Moncton', 'America/Montreal', 'America/New_York', 'America/Nipigon', 'America/Nome',
  'America/North_Dakota/Beulah', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
  'America/Pangnirtung', 'America/Phoenix', 'America/Puerto_Rico', 'America/Rainy_River',
  'America/Rankin_Inlet', 'America/Regina', 'America/Resolute', 'America/Shiprock', 'America/Sitka',
  'America/St_Johns', 'America/St_Thomas', 'America/Swift_Current', 'America/Thunder_Bay', 'America/Toronto',
  'America/Vancouver', 'America/Virgin', 'America/Whitehorse', 'America/Winnipeg', 'America/Yakutat',
  'America/Yellowknife', 'Asia/Manila', 'Navajo', 'Pacific/Chuuk', 'Pacific/Guam', 'Pacific/Honolulu',
  'Pacific/Johnston', 'Pacific/Kosrae', 'Pacific/Kwajalein', 'Pacific/Majuro', 'Pacific/Midway',
  'Pacific/Pago_Pago', 'Pacific/Palau', 'Pacific/Pohnpei', 'Pacific/Ponape', 'Pacific/Saipan',
  'Pacific/Samoa', 'Pacific/Truk', 'Pacific/Wake', 'Pacific/Yap',
]);

// Whether the browser's time zone leaves room for a month-first date. Unknown, UTC and `Etc/*` carry no
// region, so they leave the language's answer alone.
export function timeZoneAllowsMonthFirst(timeZone: string | undefined): boolean {
  if (!timeZone || timeZone === 'UTC' || timeZone.startsWith('Etc/')) return true;
  return MONTH_FIRST_ZONE_PREFIXES.some((p) => timeZone.startsWith(p)) || MONTH_FIRST_ZONES.has(timeZone);
}

// The web header's date order. A browser sends a LANGUAGE (`en-US` is most English browsers' default,
// wherever they are) but no REGION setting, which is what the phones read from the OS (English + Israel →
// `en_IL` → day-first). The time zone is the browser's own region signal, so a month-first language
// reads day-first when the clock is set outside every month-first country (`en-US` + `Asia/Jerusalem`
// → `20.10`). It never turns a day-first language month-first. This is the device's setting, not the
// account's region, so the rule above against the account-region override still holds.
export function browserIsMonthFirst(language: string | undefined, timeZone: string | undefined, now: Date): boolean {
  return localeIsMonthFirst(language, now) && timeZoneAllowsMonthFirst(timeZone);
}
